import { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Buttons,
  FlexContainer,
  ModalContext,
  Modals,
  ProgressBar,
  Text,
} from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { PreviousYearStatus } from 'Containers/PreviousYears/types';
import { TaxReturnTypes } from 'Containers/Result/utils';
import { stages } from 'Constants/stages';
import { routes } from 'Constants/routes';
import { TAXDOWN_ASSETS_DOMAIN } from 'Constants/domains';
import * as Styled from './styles';
import {
  useFetchGetXmlRecord,
  useFetchPollingQuizAnualDeclaration,
  useFetchPollingQuizPreviousYears,
  useLoaderProgress,
  useFetchGetRecordResultXml,
} from './hooks';

const QuizLoaderBanner = (): JSX.Element => {
  const { progress, timeRemaining } = useLoaderProgress();
  const isPreviousYears = useSelector(routeStateSelector);
  const { showModal, hideModal } = useContext(ModalContext);
  const { t } = useTranslation(locizeNS.DG);
  const { data } = useFetchPollingQuizAnualDeclaration(!isPreviousYears?.products?.isPreviousYear);
  const { data: xmlRecord } = useFetchGetXmlRecord(!isPreviousYears?.products?.isPreviousYear);
  const { data: resultXml } = useFetchGetRecordResultXml(Boolean(xmlRecord?.ready));
  const { data: previousYearInfo } = useFetchPollingQuizPreviousYears(
    Boolean(isPreviousYears?.products?.isPreviousYear)
  );

  const downloadData =
    data?.data?.item?.stage &&
    [stages.DATA_PARTIAL_PULLED, stages.DATA_PULLED].includes(data?.data?.item?.stage?.value);

  const downloadPreviousYearData = previousYearInfo?.data?.item?.stage === PreviousYearStatus.READY;

  const tContext = isPreviousYears?.products?.isPreviousYear ? 'PreviousYears' : '';
  const isXmlRecordReadyAndIsToReturn =
    xmlRecord?.ready &&
    resultXml?.taxReturnType === TaxReturnTypes.TO_RETURN &&
    resultXml?.flexUser;

  const routeToRedirect = isXmlRecordReadyAndIsToReturn
    ? routes.RESULT_XML
    : downloadData
      ? routes.PERSONAL_START
      : routes.PREVIOUS_YEARS;

  const handleClickModal = (): void => {
    hideModal();
    history.push(routeToRedirect);
  };

  useEffect(() => {
    if (downloadData || downloadPreviousYearData || isXmlRecordReadyAndIsToReturn) {
      showModal({
        comp: (
          <Modals.Centered
            buttonsAlign="center"
            maxSize="XL"
            hasClose={false}
            title={t('Dg.data.modal.title')}
            subtitle={t('Dg.data.modal.subtitle')}
            description={t('Dg.data.modal.infotitle', {
              context: tContext,
            })}
            externalButton={{
              label: t('Dg.data.modal.button.cancel'),
              onClick: hideModal,
            }}
            urlImage={`${TAXDOWN_ASSETS_DOMAIN}/illustrations/default/done_s.png`}
            primaryButton={{
              label: t('Dg.data.modal.button'),
              onClick: handleClickModal,
            }}
          />
        ),
      });
    }
  }, [downloadData, downloadPreviousYearData, isXmlRecordReadyAndIsToReturn]);

  return (
    <Styled.LoaderBanner direction="column" align="center" justify="center" gap="dimension_08">
      {downloadData || downloadPreviousYearData || isXmlRecordReadyAndIsToReturn ? (
        <Styled.LoaderContainer direction="row" align="center" justify="center" gap="dimension_08">
          <FlexContainer direction="column">
            <Text type="paragraphs" color="dark" size="M_L_M">
              {t('Quiz.Progress.title.finish')}
            </Text>
            <Styled.ProgressBarContainer>
              <ProgressBar filled={100} />
            </Styled.ProgressBarContainer>
          </FlexContainer>
          <Buttons.Classic
            size="xs"
            mode="primary"
            label={t('Quiz.Progress.finish.button', {
              context: tContext,
            })}
            onClick={() => history.push(routeToRedirect)}
          />
        </Styled.LoaderContainer>
      ) : (
        <Styled.LoaderContainer direction="column" align="flex-start" gap="dimension_08">
          <Text type="paragraphs" color="dark" size="M_L_M">
            {t('Quiz.Progress.title')}
          </Text>
          <Styled.ProgressBarContainer>
            <ProgressBar filled={progress} baseColor="" filledColor="" />
          </Styled.ProgressBarContainer>
          <Text type="paragraphs" color="grey" size="XS_S_S">
            {t('Quiz.Progress.progress', {
              progress,
              time: timeRemaining,
            })}
          </Text>
        </Styled.LoaderContainer>
      )}
    </Styled.LoaderBanner>
  );
};

export default QuizLoaderBanner;
